import React, { useEffect, useState } from 'react';
import config from '../Config';

/**
 * Handles the GitHub App installation callback.
 *
 * GitHub redirects here after the user installs the App:
 *   /github/callback?installation_id=xxx&state=yyy&setup_action=install
 *
 * This page calls the backend to store the installation_id, then closes itself
 * so the opener window's polling picks up the new connected state.
 *
 * Set your GitHub App's "Callback URL" to:
 *   - Local dev:   http://localhost:3000/github/callback
 *   - Production:  https://<your-frontend-domain>/github/callback
 */
export default function GitHubCallback() {
  const [status, setStatus] = useState('connecting'); // 'connecting' | 'success' | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const installationId = params.get('installation_id');
    const state = params.get('state');

    if (!installationId || !state) {
      setStatus('error');
      setMessage('Missing installation_id or state in the redirect URL.');
      return;
    }

    const base = config.RUNNER_API;
    fetch(`${base}/v1/github/callback?installation_id=${installationId}&state=${encodeURIComponent(state)}`)
      .then(async (res) => {
        if (res.ok) {
          setStatus('success');
          setMessage('GitHub connected! You can close this window.');
          // Give the opener a moment to see the status update before we close.
          setTimeout(() => { try { window.close(); } catch { } }, 1500);
        } else {
          const body = await res.json().catch(() => ({}));
          setStatus('error');
          setMessage(body.detail || `Server error (${res.status}). Please try again.`);
        }
      })
      .catch((err) => {
        setStatus('error');
        setMessage(`Network error: ${err.message}`);
      });
  }, []);

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {status === 'connecting' && (
          <>
            <div style={styles.spinner} />
            <p style={styles.text}>Connecting your GitHub account…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ ...styles.icon, color: '#22c55e' }}>✓</div>
            <p style={styles.text}>{message}</p>
            <p style={styles.sub}>This window will close automatically.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ ...styles.icon, color: '#ef4444' }}>✗</div>
            <p style={styles.text}>Connection failed</p>
            <p style={styles.sub}>{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f1117',
  },
  card: {
    background: '#1c1f2e',
    border: '1px solid #2d3148',
    borderRadius: 12,
    padding: '40px 56px',
    textAlign: 'center',
    minWidth: 320,
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #2d3148',
    borderTop: '3px solid #6c63ff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 20px',
  },
  icon: {
    fontSize: 48,
    marginBottom: 16,
    fontWeight: 700,
  },
  text: {
    color: '#e2e8f0',
    fontSize: 16,
    margin: '0 0 8px',
  },
  sub: {
    color: '#94a3b8',
    fontSize: 13,
    margin: 0,
  },
};
